import type { SupabaseClient } from '@supabase/supabase-js';

export interface CollectionMemberCheckmarkCore {
    collection_id: string;
    contact_id: string;
    user_id: string;
    checked: boolean;
    checked_at?: string | null;
}

function tableMissing(err: any): boolean {
    const msg = String(err?.message || err?.details || err);
    return /relation .*collection_member_checkmarks.* does not exist/i.test(msg) || /table .*collection_member_checkmarks.* does not exist/i.test(msg);
}

export async function listCollectionMemberCheckmarksCore(
    sb: SupabaseClient,
    collectionId: string,
    userId: string
): Promise<Array<Pick<CollectionMemberCheckmarkCore, 'contact_id' | 'checked'>>> {
    if (!collectionId) throw new Error('listCollectionMemberCheckmarksCore: collectionId is required');
    if (!userId) throw new Error('listCollectionMemberCheckmarksCore: userId is required');
    const { data, error } = await sb
        .from('collection_member_checkmarks')
        .select('contact_id, checked')
        .eq('collection_id', collectionId)
        .eq('user_id', userId);
    if (error) {
        if (tableMissing(error)) {
            throw new Error('Collection checkmarks feature not initialized. Add the collection_member_checkmarks table via migration and retry.');
        }
        throw error;
    }
    return (data || []) as Array<Pick<CollectionMemberCheckmarkCore, 'contact_id' | 'checked'>>;
}

export async function setCollectionMemberCheckmarkCore(
    sb: SupabaseClient,
    params: { collectionId: string; userId: string; contactId: string; checked: boolean }
): Promise<void> {
    const { collectionId, userId, contactId, checked } = params;
    if (!collectionId || !userId || !contactId) throw new Error('setCollectionMemberCheckmarkCore: collectionId, userId, contactId are required');
    if (checked) {
        const { error } = await sb
            .from('collection_member_checkmarks')
            .upsert({
                collection_id: collectionId,
                user_id: userId,
                contact_id: contactId,
                checked: true,
                checked_at: new Date().toISOString(),
            } as any, { onConflict: 'user_id,collection_id,contact_id' } as any);
        if (error) throw error;
    } else {
        const { error } = await sb
            .from('collection_member_checkmarks')
            .delete()
            .eq('collection_id', collectionId)
            .eq('user_id', userId)
            .eq('contact_id', contactId);
        if (error) throw error;
    }
}
